import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { Camera, FileArrowDown, Trash, Warning, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * Snap a photo of the chantier's electrical panel, get an AI-audit + material
 * inventory ready for the renovation quote (Phase 3 of BKflow differentiators).
 */
export default function PanelAnalyzer({ chantierId }) {
  const [analyses, setAnalyses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await api.get(`/chantiers/${chantierId}/panel-analyses`); setAnalyses(r.data || []); }
    catch (err) { console.warn("panel analyses load failed:", err); }
  };
  useEffect(() => { if (chantierId) load(); }, [chantierId]);

  const onPick = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error("Format non supporté (JPEG/PNG/WEBP)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const r = await api.post(`/chantiers/${chantierId}/panel-analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Tableau analysé — vérifie les détails");
      await load();
      setOpenId(r.data.id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette analyse ?")) return;
    try { await api.delete(`/panel-analyses/${id}`); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const active = analyses.find((a) => a.id === openId);

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-3" data-testid="panel-analyzer">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkle size={14} weight="fill" className="text-[#005CFF]" /> Audit tableau IA
          </div>
          <div className="text-[10px] text-white/50 mt-0.5">
            Photo du tableau → détection auto des disjoncteurs, non-conformités NF C 15-100 et inventaire matériel pour devis.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid="panel-file-input"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex-1 bg-[#005CFF] hover:bg-[#0047CC] h-10 gap-2"
          data-testid="panel-capture-btn"
        >
          {uploading ? (
            <><div className="w-3.5 h-3.5 border border-white/40 border-t-white rounded-full animate-spin" /><span className="text-xs">Analyse Gemini Vision…</span></>
          ) : (
            <><Camera size={14} weight="fill" /><span className="text-xs">Photographier / analyser le tableau</span></>
          )}
        </Button>
      </div>

      {analyses.length > 0 && (
        <div className="space-y-1.5 pt-1">
          {analyses.map((a) => {
            const r = a.result || {};
            const breakers = r.breakers?.length || 0;
            const flags = r.compliance_flags?.length || 0;
            const conf = Math.round((r.confidence || 0) * 100);
            return (
              <div key={a.id} className="rounded-lg bg-black/30 border border-white/5 p-2.5 flex items-center gap-2" data-testid={`panel-analysis-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-white/50">
                    <span className="font-mono">{(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="mx-1.5">·</span>
                    <span>{a.created_by_name}</span>
                  </div>
                  <div className="text-xs mt-0.5 truncate">
                    {breakers} modules détectés
                    {flags > 0 && <span className="ml-2 text-amber-400"><Warning size={10} weight="fill" className="inline mr-0.5" />{flags} non-conformité{flags > 1 ? "s" : ""}</span>}
                    <span className="ml-2 text-white/40">· Confiance {conf}%</span>
                  </div>
                </div>
                <button onClick={() => setOpenId(a.id)} className="text-[10px] px-2 py-1 rounded bg-[#005CFF]/20 border border-[#005CFF]/30 text-[#7CB2FF] hover:bg-[#005CFF]/30" data-testid={`panel-view-${a.id}`}>
                  Voir
                </button>
                <a
                  href={`${API}/panel-analyses/${a.id}/report.pdf?auth=`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] px-2 py-1 rounded bg-white/10 border border-white/10 text-white/80 hover:bg-white/20 flex items-center gap-1"
                  data-testid={`panel-pdf-${a.id}`}
                >
                  <FileArrowDown size={10} weight="bold" /> PDF
                </a>
                <button onClick={() => del(a.id)} className="text-white/30 hover:text-red-400 p-1" data-testid={`panel-delete-${a.id}`}>
                  <Trash size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[#005CFF]" /> Audit tableau — détail
            </DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4 text-sm">
              {active.photo_path && (
                <img
                  src={`${API}/files/${active.id}?auth=`}
                  alt="Tableau"
                  className="w-full rounded-lg border border-white/10"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              {active.result?.overall_notes && (
                <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Synthèse IA</div>
                  <div className="text-xs">{active.result.overall_notes}</div>
                </div>
              )}
              {(active.result?.compliance_flags || []).length > 0 && (
                <div className="rounded-lg bg-amber-500/[0.08] border border-amber-500/30 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1 flex items-center gap-1">
                    <Warning size={11} weight="fill" /> Non-conformités NF C 15-100
                  </div>
                  <ul className="text-xs space-y-0.5 pl-3">
                    {(active.result.compliance_flags || []).map((f, i) => <li key={`flag-${i}-${(f || "").slice(0, 20)}`} className="list-disc">{f}</li>)}
                  </ul>
                </div>
              )}
              {(active.result?.breakers || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">
                    Modules détectés ({(active.result.breakers || []).length})
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-white/10">
                    <table className="w-full text-[11px]">
                      <thead className="bg-white/[0.04] text-white/60 text-[9px] uppercase tracking-widest">
                        <tr>
                          <th className="p-1.5 text-left">R/P</th>
                          <th className="p-1.5 text-left">Type</th>
                          <th className="p-1.5 text-left">Calibre</th>
                          <th className="p-1.5 text-left">Diff.</th>
                          <th className="p-1.5 text-left">Circuit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(active.result.breakers || []).map((b, i) => (
                          <tr key={`brk-${i}-${b.row || 0}-${b.position || 0}`} className="border-t border-white/5">
                            <td className="p-1.5 font-mono">{b.row}/{b.position}</td>
                            <td className="p-1.5">{(b.kind || "").replace("_", " ")}</td>
                            <td className="p-1.5">{b.calibre_a ? `${b.calibre_a} A` : "—"}</td>
                            <td className="p-1.5">{b.type_diff || "—"}{b.sensitivity_ma ? ` · ${b.sensitivity_ma} mA` : ""}</td>
                            <td className="p-1.5 truncate max-w-[180px]">{b.inferred_circuit || b.label || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(active.result?.materials_devis || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Inventaire matériel — devis rénovation</div>
                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-[11px]">
                      <thead className="bg-white/[0.04] text-white/60 text-[9px] uppercase tracking-widest">
                        <tr>
                          <th className="p-1.5 text-left">Qté</th>
                          <th className="p-1.5 text-left">Article</th>
                          <th className="p-1.5 text-right">PU HT (€)</th>
                          <th className="p-1.5 text-right">Total HT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(active.result.materials_devis || []).map((m, i) => {
                          const q = m.quantity || 1;
                          const p = m.estimated_price_eur || 0;
                          return (
                            <tr key={`devis-${i}-${(m.label || "").slice(0, 15)}`} className="border-t border-white/5">
                              <td className="p-1.5 font-mono">{q}</td>
                              <td className="p-1.5">{m.label}</td>
                              <td className="p-1.5 text-right font-mono">{p.toFixed(2)}</td>
                              <td className="p-1.5 text-right font-mono">{(q * p).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {active && (
              <a
                href={`${API}/panel-analyses/${active.id}/report.pdf?auth=`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 h-9 px-4 rounded-md bg-[#005CFF] hover:bg-[#0047CC] text-white text-xs font-semibold"
                data-testid="panel-download-pdf"
              >
                <FileArrowDown size={12} weight="bold" /> Télécharger PDF audit
              </a>
            )}
            <Button variant="ghost" onClick={() => setOpenId(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
